// Plaid Link handoff via the hosted SDK.
//
// Flow: the backend mints a link_token, the Plaid Link SDK opens the institution login
// UI, and on success returns a public_token which we exchange server-side for a persisted
// item. The browser never sees the Plaid client_id/secret; the link_token is the only
// credential it holds.
//
// A consent gate guards the whole flow (data-collection consent). The component owns the
// link_token lifecycle so usePlaidLink has a token at render time.

import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";

import { useApi } from "../api/useApi";
import { useStepUp } from "../auth/StepUp";
import { Button } from "@/components/ui/button";

export function PlaidConnect({
  consented,
  onLinked,
}: {
  consented: boolean;
  onLinked: (institution: string | null) => void;
}) {
  const api = useApi();
  const { runWithStepUp, dialog } = useStepUp();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const exchange = useCallback(
    async (publicToken: string) => {
      setBusy(true);
      setStatus("Linking your account…");
      try {
        const resp = await runWithStepUp(() => api.exchangePlaidToken(publicToken));
        if (resp.linked) {
          setStatus(`Linked ${resp.institution_name ?? "your brokerage"}.`);
          onLinked(resp.institution_name ?? null);
        } else {
          setStatus("Plaid returned, but the account was not linked.");
        }
      } catch (err) {
        setStatus(err instanceof Error ? err.message : "Could not link the account");
      } finally {
        setBusy(false);
        setLinkToken(null);
      }
    },
    [api, onLinked, runWithStepUp],
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken) => {
      if (publicToken) {
        void exchange(publicToken);
      } else {
        setStatus("Plaid returned without a public token. Please try again.");
        setLinkToken(null);
      }
    },
    onExit: (err) => {
      // The user closed Link or it errored; surface a message but keep the page usable.
      setStatus(
        err ? `Plaid Link closed: ${err.display_message ?? err.error_message ?? err.error_code}` : "",
      );
      setLinkToken(null);
    },
  });

  // Create a fresh link_token, then open Link once the SDK is ready for it.
  const connect = useCallback(async () => {
    setStatus("");
    setBusy(true);
    try {
      const resp = await runWithStepUp(() => api.createLinkToken());
      setLinkToken(resp.link_token);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Failed to start Plaid");
      setBusy(false);
    }
  }, [api, runWithStepUp]);

  // Open the Link UI once a token is set and the SDK is ready for it.
  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="secondary"
        disabled={!consented || busy}
        onClick={() => void connect()}
        data-testid="plaid-connect"
      >
        {busy ? "Connecting…" : "Connect with Plaid"}
      </Button>
      {status && <p className="status text-sm text-muted-foreground">{status}</p>}
      {dialog}
    </div>
  );
}
